import { Cross, HeartHandshake, BookOpen, HandHeart, Quote, ArrowRight } from "lucide-react";
import { ENAGA_PHOTOS } from "@/lib/localMedia";
import { useDonation } from "@/context/DonationContext";

const TRES_FRENTES = [
  {
    icon: BookOpen,
    title: "Discipulado",
    desc: "Formação cristã viva e prática, que acompanha cada fase da vida. Através de grupos, mentoria e relacionamento, fortalecemos a fé, orientamos decisões e ajudamos crianças, jovens e famílias a construírem uma vida firmada em princípios e propósito.",
  },
  {
    icon: HandHeart,
    title: "Oração",
    desc: "Cremos no poder da oração como fundamento de tudo. Mantemos encontros constantes de intercessão, aconselhamento pastoral e cobertura espiritual pelas famílias. Cada história é colocada diante de Deus com fé, cuidado e esperança de transformação.",
  },
  {
    icon: HeartHandshake,
    title: "Acolhimento",
    desc: "Espaço seguro para quem chega ferido, cansado ou sem direção. Aqui, cada pessoa é recebida com amor, escuta verdadeira e cuidado intencional. Acolhemos histórias, restauramos dignidade e caminhamos juntos desde o primeiro passo, com respeito, atenção e propósito.",
  },
];

export default function PilarCristao() {
  const { openModal } = useDonation();

  return (
    <section
      id="pilar-cristao"
      data-testid="pilar-cristao-section"
      className="section-pad bg-background relative overflow-hidden"
    >
      <div className="absolute top-20 right-0 w-96 h-96 rounded-full bg-ga-amber/10 blur-3xl" />

      <div className="container-ga relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="pill border-ga-amber/30 text-ga-amber_deep bg-ga-amber/10 mb-6" data-testid="pilar-cristao-pill">
              <Cross className="w-3.5 h-3.5" />
              Pilar Cristão
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ga-ink">
              Antes de qualquer projeto,{" "}
              <span className="gradient-text-ga">pessoas</span>.
            </h2>
            <p className="mt-6 text-ga-ink/70 text-lg leading-relaxed">
              O cuidado espiritual é a base de tudo o que fazemos. Vidas transformadas transformam comunidades. Começamos cada atendimento pelo coração.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Real photo block */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3] shadow-xl">
              <img
                src={ENAGA_PHOTOS.oracaoCristao}
                alt="Equipe do Instituto em círculo de oração com voluntários e famílias"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-ga-amber">
                  Acolhimento em ação
                </div>
                <div className="font-display font-bold text-xl mt-1">
                  Recebemos cada criança em um ambiente seguro, preparado para
                  cuidar.
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="mt-6 rounded-[2rem] bg-white border-2 border-ga-amber/30 p-7 relative" data-testid="testemunho-cristao">
              <Quote className="absolute top-5 left-5 w-10 h-10 text-ga-amber/30" />
              <blockquote className="pl-14 text-lg text-ga-ink leading-relaxed italic">
                Cheguei sem esperança. Fui recebida com oração, com escuta e com
                um lugar onde meu filho foi tratado com dignidade. Hoje, minha
                família inteira está de pé de novo.
              </blockquote>
              <figcaption className="pl-14 mt-4 text-sm text-ga-ink/60">
                <div><strong className="text-ga-ink">Patrícia, 38 anos</strong></div>
                <div>Mãe atendida pelo Instituto</div>
              </figcaption>
            </div>
          </div>

          {/* Three frentes */}
          <div className="lg:col-span-5 order-1 lg:order-2 space-y-4">
            {TRES_FRENTES.map((f, i) => (
              <article
                key={f.title}
                data-testid={`cristao-frente-${i}`}
                className="group flex gap-5 rounded-3xl bg-white border border-border p-6 hover:border-ga-amber transition-colors"
              >
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-ga-amber/10 text-ga-amber_deep flex items-center justify-center group-hover:bg-ga-amber group-hover:text-white transition-colors">
                  <f.icon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-xl text-ga-ink">
                    {f.title}
                  </h3>
                  <p className="mt-1.5 text-ga-ink/70 text-[15px] leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </article>
            ))}

            <button
              type="button"
              onClick={() => openModal("cristao")}
              data-testid="cristao-cta-donate"
              className="btn-primary w-full"
            >
              Apoiar essa missão
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
